import React, { useState, useEffect } from 'react';
// import MUI components
import { Grid2 as Grid, Alert, Snackbar } from '@mui/material';
// import custom components
import { BoxCenterStart } from '../style/box';
import { TypographyBold } from '../style/typography';
import { commonStyles } from "../style/common";
import CommonCard from '../components/common_card';
import { BaseDialog } from '../components/base_dialog';
import DepartmentUpdate from '../components/department_update';
import Caution from '../components/caution';
import { validationSchemaEditDepartment } from '../func/validation_check';
import { fetchData, postData, postCreateFileNoData } from "../func/api";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import SkeletonLoading from "../components/skeleton_loading";
import { useAuth } from '../contexts/AuthContext';
import { REPORT_STATUS, ROLE_UUID } from '../const/config';


const MstDepartmentList = () => {
    const [department, setDepartment] = useState([]);
    const [editDepartment, setEditDepartment] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState(null);
    const [dialogData, setDialogData] = useState(null);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertSeverity, setAlertSeverity] = useState("success");
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isDisabled, setIsDisabled] = useState(false);
    const { currentUser, setCurrentUser } = useAuth();

    const { control, handleSubmit, reset, getValues, trigger } = useForm({
        resolver: yupResolver(validationSchemaEditDepartment),
        defaultValues: {
            editDepartment: {
                name: ""
            }
        },
        mode: "onBlur"
    });

    // データ取得
    useEffect(() => {
        const fetchDataAll = async () => {
            try {
                const department = await fetchData(`api/get_department`);
                setDepartment(department);

                if (department) {
                    reset({
                        editDepartment: {
                            name: department.name
                        }
                    });
                }

                // 地震一覧データ取得
                const earthquakeData = await fetchData(`api/get_earthquake_list/`);
                // 業務対中の場合は、編集不可
                const reportingData = earthquakeData.find(obj =>
                    obj.report_status_uuid === REPORT_STATUS.UNSUPPORTED ||
                    obj.report_status_uuid === REPORT_STATUS.FISRTREPORT ||
                    obj.report_status_uuid === REPORT_STATUS.PRIMARYCHECK ||
                    obj.report_powerplant_status_uuid === REPORT_STATUS.POWERPLANTSUPPORT
                );
                setIsDisabled(reportingData ? true : false);
            } catch (error) {
                console.error("データ取得中にエラーが発生しました:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchDataAll();
    }, [reset, refreshFlag]);

    // 編集対象の部署名が変わったらフォームをリセット
    useEffect(() => {
        if (editDepartment) {
            reset({
                editDepartment: {
                    name: editDepartment.name
                }
            });
        }
    }, [editDepartment, reset]);

    const openDialog = (type, data = null) => {
        setDialogType(type);
        setDialogData(data);
        setDialogOpen(true);
    };

    const handleDialogConfirm = () => {
        switch (dialogType) {
            case 'save':
                handleSubmit(handleConfirm()); // 更新処理
                break;
            default:
                break;
        }
        setDialogOpen(false);
    };

    const handleDialogCancel = () => {
        setDialogOpen(false);
    };

    // 「部署名を更新」ボタンのダイアログ「OK」押下時
    const handleConfirm = async () => {
        try {
            await postData("api/post_department", {
                uuid: dialogData.uuid,
                name: dialogData.name
            });

            const data = await postCreateFileNoData("/api/change_department_report");

            const url = window.URL.createObjectURL(new Blob([data], { type: 'application/zip' }));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "files.zip");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setAlertMessage("部署名の更新が完了しました。<br />変更の詳細はダウンロードされたファイルをご確認ください。");
            setAlertSeverity("success");
            setAlertOpen(true);
            setRefreshFlag(prev => !prev);

            // セッション情報のdepartmentを更新
            setCurrentUser({
                ...currentUser,
                department: dialogData.name
            });
        } catch (err) {
            setAlertMessage("部署名の更新に失敗しました。");
            setAlertSeverity("error");
            setAlertOpen(true);
        } finally {
            setEditDepartment(null);
            setDialogOpen(false);
        }
    };

    if (loading) {
        return <SkeletonLoading />;
    }

    return (
        <BoxCenterStart sx={commonStyles.page}>
            <Grid
                container
                spacing={7}
                columns={12}
                sx={commonStyles.container}
            >
                {/* Page Title */}
                <Grid size={{ xs: 12 }}>
                    <CommonCard
                        size={{ xs: 12 }}
                        child={
                            <TypographyBold
                                sx={{ textAlign: 'center', padding: '10px 0' }}
                            >
                                部署名管理
                            </TypographyBold>
                        }
                    ></CommonCard>
                </Grid>
                {/* 部署名変更 */}
                <DepartmentUpdate
                    title={"部署名変更"}
                    control={control}
                    department={department}
                    openDialog={openDialog}
                    trigger={trigger}
                    getValues={getValues}
                    isDisabled={isDisabled}
                />

                {/* 部署名適用箇所 */}
                <Caution
                    title={"部署名適用箇所"}
                    alt={"様式１（20240701改正：新様式）.xlsの変更箇所"}
                />

                {/* 確認ダイアログ */}
                <BaseDialog
                    open={dialogOpen}
                    onClose={handleDialogCancel}
                    title={
                        dialogType === "save" ? "部署名更新" : ""
                    }
                    message={
                        dialogType === "save" ? "部署名を更新します。よろしいですか？" : ""
                    }
                    onConfirm={handleDialogConfirm}
                    confirmText={
                        dialogType === "save" ? "更新" : "OK"
                    }
                    cancelText="キャンセル"
                />
                {/* 失敗時のSnackbar */}
                {alertMessage && (
                    <Snackbar
                        open={alertOpen}
                        autoHideDuration={3000}
                        onClose={() => setAlertOpen(false)}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        <Alert severity={alertSeverity} onClose={() => setAlertOpen(false)}>
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: alertMessage,
                                }}
                            />
                        </Alert>
                    </Snackbar>
                )}
            </Grid>
        </BoxCenterStart>
    );
};

export default MstDepartmentList;